import { useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useMemo } from 'react';
import { useDeleteNews, useGetNewsList } from "../../../queries/knowledgeBoard";
import { ActionButton } from '../../../components/Forms/ActionButton';
const EditIcon = require("../../../utils/media/icons/edit_icon.png");

const ViewNewsList = () => {

    const navigate = useNavigate();
    const { data, isPending } = useGetNewsList();
    const { mutateAsync: deleteData, isPending: isDeleting } = useDeleteNews();

    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        fromDate: '',
        toDate: '',
        status: '',
        title: '',
    });

    useEffect(() => {
        if (data) {
            setFilteredData(data);
        }
    }, [data]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = data;

        if (filters.fromDate) {
            filtered = filtered.filter((news) =>
                new Date(news.createdAt) >= new Date(filters.fromDate)
            );
        }

        if (filters.toDate) {
            filtered = filtered.filter((news) =>
                new Date(news.createdAt) <= new Date(filters.toDate)
            );
        }

        if (filters.status) {
            const isActive = filters.status === 'active';
            filtered = filtered.filter((news) => news.isActive === isActive);
        }

        if (filters.title) {
            filtered = filtered.filter((news) =>
                news.title.toLowerCase().includes(filters.title.toLowerCase())
            );
        }

        setFilteredData(filtered);
    };

    const columnHelper = createColumnHelper();

    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this news?");
        if (!confirm) return;
        if (isDeleting) return;
        try {
            await deleteData(id, {
                onSuccess: () => {
                    toast.success("News deleted successfully");
                },
                onError: () => {
                    toast.error("Failed to delete news");
                }
            });
        } catch (error) {
            toast.error("Failed to delete news");
        }
    }

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('title', {
            header: 'Title',
        }),
        columnHelper.accessor('description', {
            header: 'Description',
            cell: info => (
                <div
                    className="line-clamp-1"
                    dangerouslySetInnerHTML={{ __html: info.getValue() || 'N/A' }}
                />
            ),
        }),
        columnHelper.accessor("createdAt", {
            header: "Created At",
            align: "center",
            cell: (info) => (
                <div className="text-center">
                    {new Date(info.getValue()).toLocaleDateString("en-IN", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    })}
                    <br />
                    <small className="text-secondary">
                        {new Date(info.getValue()).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </small>
                </div>
            ),
        }),
        columnHelper.accessor('isActive', {
            header: 'Status',
            size: 10,
            align: 'center',
            cell: info => <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />,
            filterFn: 'equals',
            meta: {
                filterVariant: 'select',
                filterOptions: [
                    { value: true, label: 'Active' },
                    { value: false, label: 'Inactive' }
                ]
            }
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            enableColumnFilter: false,
            align: 'center',
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/knowledge-board/news/create-news', { state: { id: info.row.original?._id } })}
                        >
                            <img src={EditIcon} alt="view" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => {
                                handleDelete(info.row.original._id);
                            }}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
        }),
    ], [navigate]);

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of News</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] "
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                    <ActionButton
                        variant={'reset'}
                        text={'Create New News'}
                        handleSubmit={() => navigate('/knowledge-board/news/create-news')}
                    />
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                From Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="fromDate"
                                    value={filters.fromDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                To Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="toDate"
                                    value={filters.toDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="title" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                News Title
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="title"
                                    value={filters.title}
                                    onChange={handleFilterChange}
                                    placeholder="Enter News Title"
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                fromDate: '',
                                toDate: '',
                                status: '',
                                title: '',
                            });
                            setFilteredData(data);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
                    <h1 className="self-start text-lg font-bold pb-4">List of News</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No News available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </main>
    );
}

export default ViewNewsList;